import { useState } from 'react';
import { canAdministerShiftAccounts } from 'shared-models';
import type { ShiftAccountState } from 'shared-models';
import { useShiftAuth } from '../../../contexts/ShiftAuthProvider';
import { useAccountManagementController } from '../useAccountManagementController';
import classes from '../employee-scheduling-page.module.scss';

const permissionLabels = {canEditEmployees:'עריכת עובדים',canAssignEmployees:'שיבוץ עובדים',canManageSchedule:'ניהול סידור',canCreateEmployees:'יצירת עובדים'} as const;
const stateLabels = {active:'פעיל',dormant:'רדום',archived:'בארכיון'} as const;
export function AccountManagementPanel() {
  const {user} = useShiftAuth();
  const [open,setOpen] = useState(false);
  const allowed = !!user && canAdministerShiftAccounts(user);
  const controller = useAccountManagementController(open && allowed,user?.id);
  if (!allowed) return null;
  const account = controller.selected;
  const own = account?.shiftUserId===user.id;
  const displayedRole = controller.draft?.role ?? account?.role;
  return <section aria-label="ניהול חשבונות SHIFT">
    <button type="button" className="secondaryAction" aria-expanded={open} disabled={controller.busy}
      onClick={() => setOpen(current => !current)}>חשבונות SHIFT</button>
    {open && <>
      <div className={classes.actions}>
        <label>מצב חשבון<select aria-label="מצב חשבונות" disabled={controller.busy} value={controller.state}
          onChange={event => controller.changeState(event.currentTarget.value as ShiftAccountState | 'all')}>
          <option value="all">הכל</option>{Object.entries(stateLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <button type="button" className="secondaryAction" disabled={controller.busy} onClick={controller.refresh}>רענון חשבונות</button>
      </div>
      {controller.isLoading && <p role="status">טוען חשבונות...</p>}
      {controller.error && <p role="alert" className="errorText">{controller.error}</p>}
      {controller.membershipChanged && <p role="status">רשימת החשבונות השתנתה.</p>}
      <div className={classes.layout}>
        <aside className={classes.directory} aria-label="חשבונות">
          {controller.directory?.accounts.map(row => <button type="button" key={row.shiftUserId} className={classes.employee}
            disabled={controller.busy} aria-pressed={account?.shiftUserId===row.shiftUserId} onClick={() => controller.select(row)}>
            <strong>{row.employeeName}</strong><span>{row.username}</span><small>{stateLabels[row.state]}</small>
          </button>)}
          {controller.directory?.accounts.length===0 && <p>אין חשבונות במצב זה.</p>}
          <nav className={classes.actions} aria-label="דפי חשבונות">
            <button type="button" className="secondaryAction" disabled={controller.busy || controller.offset===0}
              onClick={() => controller.changePage(Math.max(0,controller.offset-25))}>הקודם</button>
            <button type="button" className="secondaryAction" disabled={controller.busy || !controller.directory?.pagination.hasMore}
              onClick={() => controller.changePage(controller.offset+25)}>הבא</button>
          </nav>
        </aside>
        {account && <div className={classes.editor}>
          <h2>{account.employeeName}</h2>
          {own && <p role="status">לא ניתן לערוך את החשבון שלך.</p>}
          {controller.stale && <p role="alert" className="errorText">החשבון נערך במקביל. השינויים המקומיים נשמרו.</p>}
          <label className={classes.metadata}>מצב<select aria-label="מצב חשבון" disabled={own || controller.isSaving}
            value={controller.draft?.state ?? account.state}
            onChange={event => controller.onChange({state:event.currentTarget.value as ShiftAccountState})}>
            {Object.entries(stateLabels).map(([value,label]) => <option key={value} value={value}
              disabled={value==='active' && !account.employeeIsActive}>{label}</option>)}
          </select></label>
          <label className={classes.metadata}>קבוצה<select aria-label="קבוצת חשבון" disabled={own || controller.isSaving}
            value={displayedRole} onChange={event => controller.onChange({role:event.currentTarget.value as 'manager' | 'worker'})}>
            <option value="worker">עובדים</option><option value="manager">מנהלים</option>
          </select></label>
          {Object.entries(permissionLabels).map(([key,label]) => <label key={key} className={classes.role}>
            <input type="checkbox" disabled={own || controller.isSaving || displayedRole==='worker'}
              checked={controller.draft?.[key as keyof typeof permissionLabels] ?? account[key as keyof typeof permissionLabels]}
              onChange={event => controller.onChange({[key]:event.currentTarget.checked})} />{label}
          </label>)}
          {controller.draft && <label className={classes.role}><input type="checkbox" checked={controller.confirmed}
            disabled={controller.isSaving} onChange={event => controller.setConfirmed(event.currentTarget.checked)} />אישור שינוי גישה והרשאות</label>}
          <div className={classes.actions}>
            <button type="button" className="primaryAction" disabled={own || controller.isSaving || !controller.draft || controller.stale || !controller.confirmed}
              onClick={() => void controller.save()}>{controller.isSaving ? 'שומר...' : 'שמירת חשבון'}</button>
            <button type="button" className="secondaryAction" disabled={!controller.draft || controller.isSaving} onClick={controller.discard}>ביטול שינוי חשבון</button>
          </div>
        </div>}
      </div>
    </>}
  </section>;
}